/**
 * ConversationProvider — wraps the dialog so a single `useConversation`
 * instance is shared between the input (which submits) and the chat
 * body (which renders messages). Without this, each component had its
 * own engine instance — optimistic user-message appends in the input
 * never showed up in the body, and early stream events arrived before
 * the body's socket subscription mounted.
 *
 * Per `apps/agent/plans/plan-embed-fixes-6-spotlight.md` chat-bug fix.
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from 'react'

import { type UploadState, useFileUpload } from '../attachments/use-file-upload'
import { useConversation, type UseConversationResult } from '../conversation/use-conversation'
import { useQueuedChat } from '../conversation/use-queued-chat'
import { useHostContext } from '../host-context'
import { useAgentStore } from '../stores/agent-store'

interface ConversationContextValue extends UseConversationResult {
  /**
   * Submit a message in the active mode. Sends immediately when idle;
   * queues while the agent is working (full-Agent-app parity, PROD-88).
   * Handles host-context, attachments, and conversation creation.
   */
  submit: (text: string) => Promise<void>
  /** Live upload state — for the input's attachment chips. */
  uploads: UploadState[]
  pickFiles: (files: File[]) => Promise<void>
  removeUpload: (localId: string) => void
  /** Queued-while-busy follow-ups — for the composer's queue chip + Esc cascade. */
  queuedTexts: string[]
  queueCount: number
  clearQueue: () => void
  popNewestQueued: () => void
  removeQueuedAt: (index: number) => void
}

const ConversationContext = createContext<ConversationContextValue | null>(null)

export function ConversationProvider({ hostApp, children }: { hostApp: string; children: ReactNode }) {
  const conversationId = useAgentStore((s) => s.conversationId)
  const setConversationId = useAgentStore((s) => s.setConversationId)
  const setComposerText = useAgentStore((s) => s.setComposerText)
  const mode = useAgentStore((s) => s.mode)
  const consumePendingSubmit = useAgentStore((s) => s.consumePendingSubmit)
  const hostContext = useHostContext()

  const conv = useConversation(conversationId)
  const {
    uploads,
    pickFiles,
    removeUpload,
    reset: resetUploads,
  } = useFileUpload({
    ensureConversation: conv.ensureConversation,
  })

  // The immediate network send — reads CURRENT attachments at call time so
  // both direct and auto-dequeued (queued-while-busy) sends pick up whatever
  // is pending in the composer when they actually fire.
  const sendNow = useCallback(
    async (text: string): Promise<string> => {
      const ready = uploads.filter((u) => u.status === 'done' && u.attachmentId)
      const attachmentIds = ready.map((u) => u.attachmentId!)
      // Persisted finalize rows ride along so the optimistic user bubble
      // renders its attachments immediately. [PROD-93]
      const attachments = ready.filter((u) => u.persisted).map((u) => u.persisted!)
      try {
        const id = await conv.send(
          text,
          hostApp,
          attachmentIds,
          'agent',
          undefined,
          hostContext ?? undefined,
          attachments,
        )
        if (!conversationId && id) {
          setConversationId(id)
        }
        resetUploads()
        return id
      } catch (err) {
        // Don't restore the composer text on error — the message is
        // already in the chat thread with a "failed to send"
        // indicator, so re-populating the composer creates a
        // confusing duplicate. The user can retry from the errored
        // message or just type a new follow-up.
        console.warn('[agent-embed] send failed', err)
        return conversationId ?? ''
      }
    },
    [conv, conversationId, hostApp, hostContext, resetUploads, setConversationId, uploads],
  )

  // [PROD-88] Queue layer — messages sent while the agent is working buffer
  // and fire as ONE merged turn when it goes idle (full-Agent-app parity).
  const queue = useQueuedChat({
    send: sendNow,
    cancel: conv.cancel,
    isBusy: conv.isBusy,
    conversationId,
  })

  const submit = useCallback(
    async (rawText: string) => {
      const text = rawText.trim()
      if (!text || mode === 'search') {
        return
      }
      setComposerText('')
      queue.enqueueOrSend(text)
    },
    [mode, queue, setComposerText],
  )

  // Consume any imperative `useAgentSubmit("…")` queued on the store —
  // ONE place handles this, not duplicated across components.
  useEffectConsumePendingSubmit(consumePendingSubmit, submit)

  // Note: terminal-error toasts (auth expired, backend unreachable)
  // were intentionally removed per user feedback — error state lives
  // entirely on the message bubble (subtle "!" + Retry button) and on
  // the optional handoff link in the chat shell. The host app's own
  // toast surface stays uncluttered.

  const value: ConversationContextValue = {
    ...conv,
    submit,
    uploads,
    pickFiles,
    removeUpload,
    queuedTexts: queue.queue,
    queueCount: queue.queueCount,
    clearQueue: queue.clearQueue,
    popNewestQueued: queue.popNewestQueued,
    removeQueuedAt: queue.removeQueuedAt,
  }

  return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>
}

export function useConversationContext(): ConversationContextValue {
  const ctx = useContext(ConversationContext)
  if (!ctx) {
    throw new Error('useConversationContext must be used inside <ConversationProvider>')
  }
  return ctx
}

// Run side-effect once on mount. Used to consume any imperative
// `useAgentSubmit("…")` that fired before the dialog opened.
function useEffectConsumePendingSubmit(
  consume: () => { text: string; mode?: unknown } | null,
  submit: (text: string) => Promise<void>,
) {
  const did = useRef(false)
  useEffect(() => {
    if (did.current) {
      return
    }
    did.current = true
    const pending = consume()
    if (pending) {
      void submit(pending.text)
    }
  }, [consume, submit])
}
